import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CreditCard, RefreshCw, Search, AlertTriangle, CheckCircle, XCircle, Download, Eye, Flag, X, Clock, ShieldCheck, AlertCircle, BarChart2, Zap, Wifi, WifiOff } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { subscribeToWebhookEvents, subscribeToVerificationLogs, fetchWebhookEvents, WEBHOOK_EVENT_LABELS, PROCESSING_STATUS_LABELS,  } from '../../../services/paystackWebhookSyncService';
import Icon from '../../../components/AppIcon';





// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatAmount = (amount, currency = 'XOF') => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency, maximumFractionDigits: 0
  })?.format(amount);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

// ─── Status configs ───────────────────────────────────────────────────────────
const VERIFICATION_STATUS = {
  matched:             { label: 'Correspondance',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  mismatched:          { label: 'Divergence',         color: 'bg-red-100 text-red-700 border-red-200',             icon: XCircle },
  missing_in_db:       { label: 'Absent en BDD',      color: 'bg-orange-100 text-orange-700 border-orange-200',    icon: AlertTriangle },
  missing_in_paystack: { label: 'Absent Paystack',    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',    icon: AlertCircle },
  disputed:            { label: 'Litige',             color: 'bg-rose-100 text-rose-700 border-rose-200',          icon: Flag },
  resolved:            { label: 'Résolu',             color: 'bg-sky-100 text-sky-700 border-sky-200',             icon: ShieldCheck },
};

const DISPUTE_REASONS = {
  amount_mismatch:        'Montant incorrect',
  status_mismatch:        'Statut divergent',
  duplicate_transaction:  'Transaction dupliquée',
  unauthorized_charge:    'Débit non autorisé',
  refund_not_processed:   'Remboursement non traité',
  other:                  'Autre',
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const VerifBadge = ({ status }) => {
  const cfg = VERIFICATION_STATUS?.[status] || { label: status, color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock };
  const Icon = cfg?.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <Icon className="w-3 h-3" />
      {cfg?.label}
    </span>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: IconComponent, label, value, color, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <IconComponent className="w-5 h-5" />
    </div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Dispute / Resolve Modal ──────────────────────────────────────────────────
const ActionModal = ({ mode, log, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('amount_mismatch');
  const [notes, setNotes] = useState('');

  const isDispute = mode === 'dispute';
  const cfg = isDispute
    ? { title: 'Signaler un litige', btnClass: 'bg-rose-600 hover:bg-rose-700', btnLabel: 'Confirmer le litige', icon: Flag, iconBg: 'bg-rose-100', iconColor: 'text-rose-600' }
    : { title: 'Résoudre le litige', btnClass: 'bg-sky-600 hover:bg-sky-700', btnLabel: 'Marquer comme résolu', icon: ShieldCheck, iconBg: 'bg-sky-100', iconColor: 'text-sky-600' };

  const Icon = cfg?.icon;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${cfg?.iconBg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${cfg?.iconColor}`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{cfg?.title}</h3>
              <p className="text-xs text-gray-400">Réf: {log?.paystack_reference}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {isDispute && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Motif du litige</label>
              <select
                value={reason}
                onChange={e => setReason(e?.target?.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              >
                {Object.entries(DISPUTE_REASONS)?.map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {isDispute ? 'Notes supplémentaires' : 'Notes de résolution'}
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e?.target?.value)}
              rows={3}
              placeholder={isDispute ? 'Décrivez le problème...' : 'Décrivez la résolution...'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
        <div className="p-6 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Annuler
          </button>
          <button
            onClick={() => onConfirm({ reason: isDispute ? reason : undefined, notes })}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${cfg?.btnClass}`}
          >
            {loading ? 'En cours...' : cfg?.btnLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Drawer ────────────────────────────────────────────────────────────
const DetailDrawer = ({ log, onClose, onDispute, onResolve }) => {
  if (!log) return null;

  const amountMatch = log?.paystack_amount === log?.db_amount;
  const statusMatch = log?.paystack_status?.toLowerCase() === log?.db_status?.toLowerCase();

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-5 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Détail de vérification</h3>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{log?.paystack_reference}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          {/* Status */}
          <div className="flex items-center gap-3">
            <VerifBadge status={log?.verification_status} />
            {log?.dispute_flag && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                <Flag className="w-3 h-3" /> Litige actif
              </span>
            )}
          </div>

          {/* Comparison table */}
          <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
            <div className="grid grid-cols-3 text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-2.5 border-b border-gray-200 bg-gray-100">
              <span>Champ</span>
              <span>Paystack</span>
              <span>Base de données</span>
            </div>
            {[
              { label: 'Montant', ps: formatAmount(log?.paystack_amount, log?.paystack_currency), db: formatAmount(log?.db_amount, log?.db_currency), match: amountMatch },
              { label: 'Statut', ps: log?.paystack_status || '—', db: log?.db_status || '—', match: statusMatch },
              { label: 'Devise', ps: log?.paystack_currency || '—', db: log?.db_currency || '—', match: log?.paystack_currency === log?.db_currency },
            ]?.map(row => (
              <div key={row?.label} className={`grid grid-cols-3 px-4 py-3 text-sm border-b border-gray-100 last:border-0 ${!row?.match ? 'bg-red-50' : ''}`}>
                <span className="font-medium text-gray-700">{row?.label}</span>
                <span className="text-gray-600 font-mono text-xs">{row?.ps}</span>
                <span className={`font-mono text-xs ${!row?.match ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{row?.db}</span>
              </div>
            ))}
          </div>

          {/* Paystack details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-blue-500" /> Données Paystack
            </h4>
            <div className="space-y-2">
              {[
                { label: 'Canal', value: log?.paystack_channel || '—' },
                { label: 'Date paiement', value: formatDate(log?.paystack_paid_at) },
                { label: 'Vérifié le', value: formatDate(log?.verified_at) },
              ]?.map(item => (
                <div key={item?.label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{item?.label}</span>
                  <span className="text-gray-800 font-medium">{item?.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dispute info */}
          {log?.dispute_flag && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-2">
              <h4 className="text-sm font-semibold text-rose-700 flex items-center gap-2">
                <Flag className="w-4 h-4" /> Informations du litige
              </h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-rose-600">Motif</span>
                  <span className="text-rose-800 font-medium">{DISPUTE_REASONS?.[log?.dispute_reason] || log?.dispute_reason || '—'}</span>
                </div>
                {log?.dispute_notes && (
                  <p className="text-rose-700 text-xs mt-2 bg-rose-100 rounded-lg p-2">{log?.dispute_notes}</p>
                )}
                <div className="flex justify-between">
                  <span className="text-rose-600">Signalé le</span>
                  <span className="text-rose-800 font-medium">{formatDate(log?.dispute_raised_at)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Resolution info */}
          {log?.verification_status === 'resolved' && log?.resolution_notes && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-sky-700 flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4" /> Résolution
              </h4>
              <p className="text-sm text-sky-700">{log?.resolution_notes}</p>
              <p className="text-xs text-sky-500 mt-1">{formatDate(log?.resolved_at)}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
          {!log?.dispute_flag && log?.verification_status !== 'resolved' && (
            <button
              onClick={() => onDispute(log)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Flag className="w-4 h-4" /> Signaler litige
            </button>
          )}
          {(log?.dispute_flag || log?.verification_status === 'disputed') && log?.verification_status !== 'resolved' && (
            <button
              onClick={() => onResolve(log)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <ShieldCheck className="w-4 h-4" /> Résoudre
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Realtime Event Toast ─────────────────────────────────────────────────────
const RealtimeToast = ({ events, onDismiss }) => {
  if (!events?.length) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 space-y-2 max-w-sm">
      {events?.map((evt, i) => {
        const cfg = WEBHOOK_EVENT_LABELS?.[evt?.event_type] || { label: evt?.event_type, color: 'bg-gray-100 text-gray-700', icon: '📩' };
        return (
          <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border border-white/20 ${cfg?.color} animate-pulse`}>
            <span className="text-lg leading-none">{cfg?.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold">{cfg?.label}</p>
              {evt?.paystack_reference && (
                <p className="text-xs font-mono truncate opacity-75">{evt?.paystack_reference}</p>
              )}
            </div>
            <button onClick={() => onDismiss(i)} className="opacity-60 hover:opacity-100 transition-opacity">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ─── Webhook Events Feed ──────────────────────────────────────────────────────
const WebhookEventsFeed = ({ events, loading }) => {
  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d)?.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-500" />
          Événements Webhook en temps réel
        </h3>
        <span className="text-xs text-gray-400">{events?.length} événement{events?.length !== 1 ? 's' : ''}</span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 text-blue-400 animate-spin mr-2" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : events?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Zap className="w-8 h-8 text-gray-200 mb-2" />
          <p className="text-sm text-gray-400">En attente d'événements Paystack...</p>
          <p className="text-xs text-gray-300 mt-1">Les événements apparaîtront ici en temps réel</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
          {events?.map(evt => {
            const cfg = WEBHOOK_EVENT_LABELS?.[evt?.event_type] || { label: evt?.event_type, color: 'bg-gray-100 text-gray-600', icon: '📩' };
            const statusCfg = PROCESSING_STATUS_LABELS?.[evt?.processing_status] || { label: evt?.processing_status, color: 'bg-gray-100 text-gray-500' };
            return (
              <div key={evt?.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                <span className="text-base leading-none flex-shrink-0">{cfg?.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cfg?.color}`}>
                      {cfg?.label}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg?.color}`}>
                      {statusCfg?.label}
                    </span>
                  </div>
                  {evt?.paystack_reference && (
                    <p className="text-xs font-mono text-gray-400 mt-0.5 truncate">{evt?.paystack_reference}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{formatDate(evt?.received_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const PaystackVerificationPanel = () => {
  const [logs, setLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [disputeFilter, setDisputeFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [actionModal, setActionModal] = useState(null); // { mode: 'dispute'|'resolve', log }
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, matched: 0, mismatched: 0, disputed: 0, resolved: 0, missing: 0 });

  // ── Realtime state ───────────────────────────────────────────────────────
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookEventsLoading, setWebhookEventsLoading] = useState(true);
  const [toastEvents, setToastEvents] = useState([]);
  const [showEventsFeed, setShowEventsFeed] = useState(false);
  const [newEventsCount, setNewEventsCount] = useState(0);
  const unsubscribeWebhookRef = useRef(null);
  const unsubscribeVerifRef = useRef(null);

  // ── Fetch verification logs ──────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        ?.from('paystack_verification_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(200);
      if (err) throw err;
      setLogs(data || []);
      computeStats(data || []);
    } catch (e) {
      setError(e?.message || 'Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch payments from Supabase ─────────────────────────────────────────
  const fetchPayments = useCallback(async () => {
    try {
      const { data } = await supabase
        ?.from('payments')
        ?.select('id, amount, status, paystack_reference, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(500);
      setPayments(data || []);
    } catch (_) {}
  }, []);

  // ── Fetch webhook events ─────────────────────────────────────────────────
  const loadWebhookEvents = useCallback(async () => {
    setWebhookEventsLoading(true);
    try {
      const events = await fetchWebhookEvents({ limit: 30 });
      setWebhookEvents(events);
    } catch (_) {
    } finally {
      setWebhookEventsLoading(false);
    }
  }, []);

  const computeStats = (data) => {
    const s = { total: data?.length, matched: 0, mismatched: 0, disputed: 0, resolved: 0, missing: 0 };
    data?.forEach(l => {
      if (l?.verification_status === 'matched') s.matched++;
      else if (l?.verification_status === 'mismatched') s.mismatched++;
      else if (l?.verification_status === 'disputed') s.disputed++;
      else if (l?.verification_status === 'resolved') s.resolved++;
      else if (l?.verification_status === 'missing_in_db' || l?.verification_status === 'missing_in_paystack') s.missing++;
    });
    setStats(s);
  };

  // ── Setup realtime subscriptions ─────────────────────────────────────────
  useEffect(() => {
    fetchLogs();
    fetchPayments();
    loadWebhookEvents();

    // Subscribe to new webhook events
    const unsubWebhook = subscribeToWebhookEvents((update) => {
      if (update?.type === 'new_event') {
        const newEvt = update?.data;
        setWebhookEvents(prev => [newEvt, ...prev?.slice(0, 29)]);
        setToastEvents(prev => [newEvt, ...prev?.slice(0, 2)]);
        setNewEventsCount(prev => prev + 1);
        setRealtimeConnected(true);
        // Auto-dismiss toast after 5s
        setTimeout(() => {
          setToastEvents(prev => prev?.filter((_, i) => i !== 0));
        }, 5000);
      }
    });
    unsubscribeWebhookRef.current = unsubWebhook;

    // Subscribe to verification log changes (auto-refresh on webhook sync)
    const unsubVerif = subscribeToVerificationLogs((update) => {
      if (update?.type === 'INSERT' || update?.type === 'UPDATE') {
        setRealtimeConnected(true);
        fetchLogs();
      }
    });
    unsubscribeVerifRef.current = unsubVerif;

    // Mark realtime as connected
    setRealtimeConnected(true);

    return () => {
      unsubscribeWebhookRef?.current?.();
      unsubscribeVerifRef?.current?.();
    };
  }, [fetchLogs, fetchPayments, loadWebhookEvents]);

  // ── Run verification: compare payments vs logs ───────────────────────────
  const runVerification = async () => {
    setVerifying(true);
    setError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Build a map of existing verification logs by reference
      const existingRefs = new Set(logs.map(l => l.paystack_reference));

      // For each payment not yet verified, create a verification log
      const toInsert = payments?.filter(p => p?.paystack_reference && !existingRefs?.has(p?.paystack_reference))?.map(p => {
          // Simulate Paystack data from payment metadata or payment record
          const paystackAmount = p?.amount;
          const paystackStatus = p?.status;
          const paystackCurrency = 'XOF';
          let verificationStatus = 'matched';
          if (!paystackAmount && !paystackStatus) {
            verificationStatus = 'missing_in_paystack';
          } else if (
            Math.abs((paystackAmount || 0) - (p?.amount || 0)) > 1 ||
            (paystackStatus || '')?.toLowerCase() !== (p?.status || '')?.toLowerCase()
          ) {
            verificationStatus = 'mismatched';
          }

          return {
            payment_id: p?.id,
            paystack_reference: p?.paystack_reference,
            paystack_amount: paystackAmount,
            paystack_status: paystackStatus,
            paystack_currency: paystackCurrency,
            paystack_paid_at: p?.created_at,
            db_amount: p?.amount,
            db_status: p?.status,
            db_currency: paystackCurrency,
            verification_status: verificationStatus,
            dispute_flag: verificationStatus === 'mismatched',
            verified_by: user?.id ?? null,
            verified_at: new Date()?.toISOString(),
          };
        });

      if (toInsert?.length > 0) {
        const { error: insertErr } = await supabase
          ?.from('paystack_verification_logs')
          ?.insert(toInsert);
        if (insertErr) throw insertErr;
      }

      await fetchLogs();
    } catch (e) {
      setError(e?.message || 'Erreur lors de la vérification');
    } finally {
      setVerifying(false);
    }
  };

  // ── Dispute action ───────────────────────────────────────────────────────
  const handleDispute = async ({ reason, notes }) => {
    setActionLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error: err } = await supabase
        ?.from('paystack_verification_logs')
        ?.update({
          dispute_flag: true,
          verification_status: 'disputed',
          dispute_reason: reason,
          dispute_notes: notes,
          dispute_raised_by: user?.id,
          dispute_raised_at: new Date()?.toISOString(),
        })
        ?.eq('id', actionModal?.log?.id);
      if (err) throw err;
      setActionModal(null);
      setSelectedLog(null);
      await fetchLogs();
    } catch (e) {
      setError(e?.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Resolve action ───────────────────────────────────────────────────────
  const handleResolve = async ({ notes }) => {
    setActionLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error: err } = await supabase
        ?.from('paystack_verification_logs')
        ?.update({
          verification_status: 'resolved',
          dispute_flag: false,
          resolved_by: user?.id,
          resolved_at: new Date()?.toISOString(),
          resolution_notes: notes,
        })
        ?.eq('id', actionModal?.log?.id);
      if (err) throw err;
      setActionModal(null);
      setSelectedLog(null);
      await fetchLogs();
    } catch (e) {
      setError(e?.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Export audit log as CSV ──────────────────────────────────────────────
  const exportCSV = () => {
    const headers = [
      'Référence Paystack', 'Statut Vérification', 'Montant Paystack', 'Montant BDD',
      'Statut Paystack', 'Statut BDD', 'Devise', 'Canal', 'Litige', 'Motif Litige',
      'Notes Litige', 'Date Paiement', 'Date Vérification'
    ];
    const rows = filteredLogs?.map(l => [
      l?.paystack_reference,
      VERIFICATION_STATUS?.[l?.verification_status]?.label || l?.verification_status,
      l?.paystack_amount ?? '',
      l?.db_amount ?? '',
      l?.paystack_status ?? '',
      l?.db_status ?? '',
      l?.paystack_currency ?? '',
      l?.paystack_channel ?? '',
      l?.dispute_flag ? 'Oui' : 'Non',
      DISPUTE_REASONS?.[l?.dispute_reason] || '',
      (l?.dispute_notes || '')?.replace(/,/g, ';'),
      l?.paystack_paid_at ? new Date(l.paystack_paid_at)?.toISOString() : '',
      l?.verified_at ? new Date(l.verified_at)?.toISOString() : '',
    ]);

    const csvContent = [headers, ...rows]?.map(row => row?.map(v => `"${v}"`)?.join(','))?.join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_paystack_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  // ── Filtering ────────────────────────────────────────────────────────────
  const filteredLogs = logs?.filter(l => {
    const matchSearch = !search ||
      l?.paystack_reference?.toLowerCase()?.includes(search?.toLowerCase()) ||
      l?.paystack_status?.toLowerCase()?.includes(search?.toLowerCase()) ||
      l?.db_status?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || l?.verification_status === statusFilter;
    const matchDispute = disputeFilter === 'all' ||
      (disputeFilter === 'disputed' && l?.dispute_flag) ||
      (disputeFilter === 'clean' && !l?.dispute_flag);
    return matchSearch && matchStatus && matchDispute;
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            Vérification Paystack
            {/* Realtime status indicator */}
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ml-1 ${
              realtimeConnected
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :'bg-gray-50 text-gray-500 border-gray-200'
            }`}>
              {realtimeConnected
                ? <><Wifi className="w-3 h-3" /> Sync temps réel</>
                : <><WifiOff className="w-3 h-3" /> Hors ligne</>
              }
            </span>
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Synchronisation automatique des événements Paystack vers Supabase
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Webhook events feed toggle */}
          <button
            onClick={() => { setShowEventsFeed(v => !v); setNewEventsCount(0); }}
            className="relative flex items-center gap-2 px-4 py-2 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-200 rounded-lg text-sm font-medium transition-colors"
          >
            <Zap className="w-4 h-4" />
            Événements
            {newEventsCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {newEventsCount > 9 ? '9+' : newEventsCount}
              </span>
            )}
          </button>
          <button
            onClick={exportCSV}
            disabled={filteredLogs?.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </button>
          <button
            onClick={runVerification}
            disabled={verifying || payments?.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${verifying ? 'animate-spin' : ''}`} />
            {verifying ? 'Vérification...' : 'Lancer la vérification'}
          </button>
        </div>
      </div>

      {/* Realtime sync info banner */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-emerald-800">Synchronisation automatique active</p>
          <p className="text-xs text-emerald-600 mt-0.5">
            Les événements Paystack (confirmation, remboursement, litige) sont automatiquement synchronisés
            via webhook sans vérification manuelle. Les tables <code className="bg-emerald-100 px-1 rounded">payments</code> et{' '}
            <code className="bg-emerald-100 px-1 rounded">escrow_transactions</code> sont mises à jour en temps réel.
          </p>
        </div>
      </div>

      {/* Webhook Events Feed (collapsible) */}
      {showEventsFeed && (
        <WebhookEventsFeed events={webhookEvents} loading={webhookEventsLoading} />
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">Erreur</p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={BarChart2}    label="Total vérifiés"  value={stats?.total}      color="bg-blue-100 text-blue-600" />
        <StatCard icon={CheckCircle}  label="Correspondances" value={stats?.matched}    color="bg-emerald-100 text-emerald-600" />
        <StatCard icon={XCircle}      label="Divergences"     value={stats?.mismatched} color="bg-red-100 text-red-600" />
        <StatCard icon={Flag}         label="Litiges actifs"  value={stats?.disputed}   color="bg-rose-100 text-rose-600" />
        <StatCard icon={ShieldCheck}  label="Résolus"         value={stats?.resolved}   color="bg-sky-100 text-sky-600" />
        <StatCard icon={AlertCircle}  label="Absents"         value={stats?.missing}    color="bg-orange-100 text-orange-600" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par référence, statut..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e?.target?.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tous les statuts</option>
            {Object.entries(VERIFICATION_STATUS)?.map(([k, v]) => (
              <option key={k} value={k}>{v?.label}</option>
            ))}
          </select>
          <select
            value={disputeFilter}
            onChange={e => setDisputeFilter(e?.target?.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tous</option>
            <option value="disputed">Avec litige</option>
            <option value="clean">Sans litige</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mr-3" />
            <span className="text-gray-500 text-sm">Chargement des logs...</span>
          </div>
        ) : filteredLogs?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CreditCard className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Aucun log de vérification</p>
            <p className="text-gray-400 text-sm mt-1">
              {logs?.length === 0
                ? 'Les logs apparaîtront automatiquement lors des événements Paystack' :'Aucun résultat pour les filtres sélectionnés'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Référence</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant Paystack</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant BDD</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Litige</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vérifié le</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLogs?.map(log => {
                  const amountMismatch = log?.paystack_amount != null && log?.db_amount != null &&
                    Math.abs(log?.paystack_amount - log?.db_amount) > 1;
                  return (
                    <tr key={log?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                          {log?.paystack_reference?.slice(0, 20)}{log?.paystack_reference?.length > 20 ? '…' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <VerifBadge status={log?.verification_status} />
                      </td>
                      <td className={`px-4 py-3 text-right font-medium tabular-nums ${amountMismatch ? 'text-red-600' : 'text-gray-700'}`}>
                        {formatAmount(log?.paystack_amount, log?.paystack_currency)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium tabular-nums ${amountMismatch ? 'text-red-600' : 'text-gray-700'}`}>
                        {formatAmount(log?.db_amount, log?.db_currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {log?.dispute_flag ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700">
                            <Flag className="w-3 h-3" /> Litige
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(log?.verified_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!log?.dispute_flag && log?.verification_status !== 'resolved' && (
                            <button
                              onClick={() => setActionModal({ mode: 'dispute', log })}
                              className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              title="Signaler litige"
                            >
                              <Flag className="w-4 h-4" />
                            </button>
                          )}
                          {(log?.dispute_flag || log?.verification_status === 'disputed') && log?.verification_status !== 'resolved' && (
                            <button
                              onClick={() => setActionModal({ mode: 'resolve', log })}
                              className="p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                              title="Résoudre"
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
              <span>
                {filteredLogs?.length} entrée{filteredLogs?.length !== 1 ? 's' : ''} affichée{filteredLogs?.length !== 1 ? 's' : ''}
                {logs?.length !== filteredLogs?.length && ` (sur ${logs?.length} total)`}
              </span>
              {realtimeConnected && (
                <span className="flex items-center gap-1.5 text-emerald-600">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  Mise à jour automatique active
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedLog && (
        <DetailDrawer
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onDispute={(log) => { setSelectedLog(null); setActionModal({ mode: 'dispute', log }); }}
          onResolve={(log) => { setSelectedLog(null); setActionModal({ mode: 'resolve', log }); }}
        />
      )}

      {/* Action Modal */}
      {actionModal && (
        <ActionModal
          mode={actionModal?.mode}
          log={actionModal?.log}
          loading={actionLoading}
          onConfirm={actionModal?.mode === 'dispute' ? handleDispute : handleResolve}
          onCancel={() => setActionModal(null)}
        />
      )}

      {/* Realtime toast notifications */}
      <RealtimeToast
        events={toastEvents}
        onDismiss={(i) => setToastEvents(prev => prev?.filter((_, idx) => idx !== i))}
      />
    </div>
  );
};

export default PaystackVerificationPanel;
